import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'
import type { AdminSelectOption } from './AdminSelect'

interface AdminSearchableSelectProps {
  value: string
  onChange: (value: string) => void
  options: AdminSelectOption[]
  placeholder?: string
  className?: string
  searchPlaceholder?: string
  size?: 'default' | 'sm'
  openUpward?: boolean
}

/**
 * Custom dropdown with an inline search box, matching the mock's
 * SearchableSelect (used for engineer/approver filters). The mock's `ink`
 * accent is mapped to the app's `gray-900`.
 */
export function AdminSearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  className = '',
  searchPlaceholder = 'Search...',
  size = 'default',
  openUpward = false,
}: AdminSearchableSelectProps) {
  const isSmall = size === 'sm'
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find((option) => option.value === value)
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (isOpen && searchInputRef.current) searchInputRef.current.focus()
  }, [isOpen])

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchQuery('')
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`relative flex w-full items-center ${isSmall ? 'h-9 px-2.5 pr-7 text-sm' : 'h-12 px-4 pr-10 text-base'} cursor-pointer rounded-xl border border-gray-300 bg-white text-left font-medium text-gray-900 transition-colors hover:border-gray-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary ${className}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption?.label || placeholder || 'Select...'}
        </span>
        <ChevronDown
          className={`absolute ${isSmall ? 'right-2 h-4 w-4' : 'right-4 h-5 w-5'} pointer-events-none top-1/2 -translate-y-1/2 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className={`absolute z-[60] w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg ${openUpward ? 'bottom-full mb-2' : 'mt-2'}`}
        >
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className={`w-full appearance-none !rounded-full !border-2 !border-gray-200 !bg-white pl-9 pr-3 shadow-sm ${isSmall ? 'py-1.5 text-sm' : 'py-2 text-sm'} text-gray-900 placeholder:text-gray-400 focus:!border-gray-300 focus:!bg-white focus:!outline-none focus:!ring-4 focus:!ring-gray-200/70`}
                onClick={(event) => event.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto" role="listbox" aria-label="Options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={`flex w-full items-center justify-between ${isSmall ? 'px-2.5 py-2 text-sm' : 'px-4 py-3 text-base'} text-left font-medium transition-colors ${isSelected ? 'bg-brand-primary text-[var(--brand-primary-text)]' : 'bg-white text-gray-900 hover:bg-gray-50'}`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span>{option.label}</span>
                    {isSelected && <Check className="h-4 w-4" />}
                  </button>
                )
              })
            ) : (
              <div className="px-4 py-6 text-center text-sm text-gray-400">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
